import React, { useEffect, useState, useCallback } from "react";
import Layout from "../../Components/Layout/Layout";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Autocomplete,
} from "@mui/material";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function ScheduleList() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [newStartTime, setNewStartTime] = useState("");
  const [newEndTime, setNewEndTime] = useState("");
  const [newDaysOfWeek, setNewDaysOfWeek] = useState([]);

  const daysOptions = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const getAuthToken = () => localStorage.getItem("auth_token") || "";

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        console.error("No auth token found in localStorage");
        setSchedules([]);
        return;
      }
      const res = await fetch(`${API_URL}/admin/schedules?auth_token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (data.status === "success") {
        // Преобразуем start_times в start_time и end_time
        const transformedSchedules = (data.schedules || []).map((schedule) => {
          const [start_time, end_time] = schedule.start_times.split(",");
          return {
            ...schedule,
            start_time,
            end_time,
          };
        });
        setSchedules(transformedSchedules);
      } else {
        console.error("Failed to fetch schedules:", data.message);
        setSchedules([]);
      }
    } catch (err) {
      console.error("Network error fetching schedules:", err);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const resetCreateForm = () => {
    setNewStartTime("");
    setNewEndTime("");
    setNewDaysOfWeek([]);
  };

  const handleCreate = () => {
    resetCreateForm();
    setOpenCreate(true);
  };

  const handleCreateSave = async () => {
    if (!newStartTime || !newEndTime || !newDaysOfWeek.length) {
      alert("Please fill in all fields");
      return;
    }
    const token = getAuthToken();
    const body = {
      auth_token: token,
      start_times: `${newStartTime},${newEndTime}`, // Формируем start_times в формате API
      days_of_week: newDaysOfWeek.join(","),
    };
    try {
      const res = await fetch(`${API_URL}/admin/schedules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchSchedules();
        setOpenCreate(false);
        resetCreateForm();
      } else {
        console.error("Create schedule error:", data.message);
        alert(`Failed to create schedule: ${data.message}`);
      }
    } catch (err) {
      console.error("Create schedule fetch error:", err);
      alert("Network error creating schedule. Please try again.");
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setNewStartTime(schedule.start_time || "");
    setNewEndTime(schedule.end_time || "");
    setNewDaysOfWeek(schedule.days_of_week ? schedule.days_of_week.split(",") : []);
    setOpenEdit(true);
  };

  const handleEditSave = async () => {
    if (!newStartTime || !newEndTime || !newDaysOfWeek.length) {
      alert("Please fill in all fields");
      return;
    }
    const token = getAuthToken();
    const body = {
      auth_token: token,
      start_times: `${newStartTime},${newEndTime}`, // Формируем start_times в формате API
      days_of_week: newDaysOfWeek.join(","),
    };
    try {
      const res = await fetch(`${API_URL}/admin/schedules/${editingSchedule.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchSchedules();
        setOpenEdit(false);
        setEditingSchedule(null);
      } else {
        console.error("Edit schedule error:", data.message);
        alert(`Failed to edit schedule: ${data.message}`);
      }
    } catch (err) {
      console.error("Edit schedule fetch error:", err);
      alert("Network error editing schedule. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this schedule?")) {
      return;
    }
    const token = getAuthToken();
    try {
      const res = await fetch(`${API_URL}/admin/schedules/${id}?auth_token=${encodeURIComponent(token)}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchSchedules();
      } else {
        console.error("Delete schedule error:", data.message);
        alert(`Failed to delete schedule: ${data.message}`);
      }
    } catch (err) {
      console.error("Network error deleting schedule:", err);
      alert("Network error deleting schedule. Please try again.");
    }
  };

  const userRole = localStorage.getItem("userRole") || "";

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5">Schedules</Typography>

        {["ceo", "admin"].includes(userRole.toLowerCase()) && (
          <Button variant="contained" sx={{ mt: 2 }} onClick={handleCreate}>
            Create Schedule
          </Button>
        )}

        {loading ? (
          <Typography sx={{ mt: 2 }}>Loading...</Typography>
        ) : schedules.length === 0 ? (
          <Typography sx={{ mt: 2 }}>No schedules available</Typography>
        ) : (
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Days of Week</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.id}</TableCell>
                  <TableCell>{s.start_time}</TableCell>
                  <TableCell>{s.end_time}</TableCell>
                  <TableCell>
                    {s.days_of_week.split(",").map((day) => (
                      <Chip key={day} label={day} sx={{ mr: 1 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      onClick={() => handleEdit(s)}
                      sx={{ mr: 1 }}
                    >
                      Edit
                    </Button>
                    <Button
                      color="error"
                      onClick={() => handleDelete(s.id)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
          <DialogTitle>Create Schedule</DialogTitle>
          <DialogContent>
            <TextField
              label="Start Time (e.g., 09:00)"
              value={newStartTime}
              onChange={(e) => setNewStartTime(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            />
            <TextField
              label="End Time (e.g., 17:00)"
              value={newEndTime}
              onChange={(e) => setNewEndTime(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            />
            <Autocomplete
              multiple
              options={daysOptions}
              value={newDaysOfWeek}
              onChange={(e, v) => setNewDaysOfWeek(v)}
              renderInput={(params) => (
                <TextField {...params} label="Days of Week" sx={{ mt: 2 }} />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateSave}>Save</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
          <DialogTitle>Edit Schedule</DialogTitle>
          <DialogContent>
            <TextField
              label="Start Time (e.g., 09:00)"
              value={newStartTime}
              onChange={(e) => setNewStartTime(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            />
            <TextField
              label="End Time (e.g., 17:00)"
              value={newEndTime}
              onChange={(e) => setNewEndTime(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            />
            <Autocomplete
              multiple
              options={daysOptions}
              value={newDaysOfWeek}
              onChange={(e, v) => setNewDaysOfWeek(v)}
              renderInput={(params) => (
                <TextField {...params} label="Days of Week" sx={{ mt: 2 }} />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleEditSave}>Save Changes</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
